import { useCallback, useMemo, useState, type ReactNode } from 'react';
import type { AssistantState } from '../../types/runtime-state';
import {
  isAppRequestMessage,
  isErrorMessage,
  isQuestionMessage,
  isScheduleMessage,
  isSaveFileReplyMessage,
  isSaveFileRequestMessage,
  isSandboxReviewMessage,
  isSecretInputMessage,
  isToolMessage,
  getTextContent,
  conversationMessageAttachmentKey,
  conversationMessageCachedAttachmentImageSize,
  conversationMessageRenderIdentity,
  type ConversationAttachmentImageLayoutHandler,
  type ConversationMessage,
  type ConversationMessageContent,
} from '../../types/conversation-message';
import { ExternalLink, KeyRound, LogIn, MapPin, Monitor } from 'lucide-react';
import { ErrorBoundary } from '../error-boundary';
import { CueStreamdown } from '../markdown/streamdown';
import { AssistantMessageOperations } from './assistant-message-operations';
import { QuestionMessage } from './question-message';
import { SaveFileMessage } from './save-file-message';
import { ScheduleMessage } from './schedule-message';
import { SandboxReviewMessage } from './sandbox-review-message';
import { SecretInputMessage } from './secret-input-message';
import { ErrorCard } from '../error-card';
import type { ErrorPayload } from '../error-card-utils';
import { WebBrowseWidget, tryParseWebBrowsePayload } from './web-browse-widget';
import {
  InteractiveLoginCard,
  tryParseInteractiveLoginPayload,
} from './interactive-login-card';
import { BookShareCard, tryParseBookSharePayload } from './book-share-card';
import { cn } from '@/utils/cn';
import { ShieldLefthalfFilledSFSymbolMedium } from '@/assets/sf-symbols/medium/shield.lefthalf.filled';
import { useT } from '@/utils/i18n';
import { ConversationAttachmentView } from './conversation-attachment-view';
import { MessageIdentityHeader } from './message-identity';
import { assistantTurnTimestamp } from './assistant-message-time';

const assistantMarkdownControls = {
  table: {
    fullscreen: false,
  },
} as const;

function openAppRequestURL(url: string) {
  const utilsBridge = window.jsb?.UtilsBridge;
  if (utilsBridge?.openURL) {
    utilsBridge.openURL(url).catch(() => {
      window.open(url, '_blank', 'noopener,noreferrer');
    });
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

type AppRequestTone = 'pending' | 'approved' | 'expired' | 'denied';

type AppRequestDecision = {
  approved: boolean;
  reason?: string;
  mode?: string;
};

export type HostAccessDecisionRequest = {
  approved: boolean;
};

export type ComputerUseStartDecisionRequest = {
  approved: boolean;
  mode?: string;
};

function normalizedStatus(content: ConversationMessageContent) {
  return content.status?.trim().toLowerCase();
}

function normalizedPreviewURL(content: ConversationMessageContent) {
  const url = content.url?.trim();
  if (!url || url.toLowerCase().startsWith('data:text/html')) {
    return null;
  }
  return url;
}

function isVerifiedPreview(content: ConversationMessageContent) {
  return content.verifiedStatus?.trim().toLowerCase() === 'ok';
}

function HTMLPreviewBlock({
  content,
}: {
  content: ConversationMessageContent;
}) {
  const url = normalizedPreviewURL(content);
  const verified = isVerifiedPreview(content);
  const isHTML = content.mimeType?.trim().startsWith('text/html') ?? false;
  const title =
    content.title?.trim() ||
    content.text?.trim() ||
    content.fileName?.trim() ||
    content.siteName?.trim() ||
    'Web preview';
  const subtitle =
    content.siteName?.trim() ||
    (verified
      ? isHTML
        ? 'Verified HTML preview'
        : 'Verified preview'
      : isHTML
        ? 'HTML artifact'
        : 'Preview artifact');
  const actionLabel = verified ? 'Open preview' : 'Open artifact';

  return (
    <div
      data-preview-card
      className={cn(
        'w-full overflow-hidden rounded-2xl border border-border bg-surface-card px-4 py-4',
        'select-none'
      )}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-fill-soft text-text-primary">
            <Monitor className="size-5" aria-hidden="true" />
          </div>
          <div className="min-w-0 space-y-1">
            <div className="truncate text-[0.9375rem] font-semibold leading-5 text-text-primary">
              {title}
            </div>
            <div className="truncate text-xs font-medium leading-4 text-text-tertiary">
              {subtitle}
            </div>
          </div>
        </div>

        {url ? (
          <button
            type="button"
            onClick={() => openAppRequestURL(url)}
            className={cn(
              'inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1.5',
              'text-[0.8125rem] font-semibold leading-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0077ff]/35',
              verified
                ? 'bg-fill-soft text-text-primary hover:bg-fill-strong'
                : 'border border-border bg-transparent text-text-secondary hover:bg-fill-soft'
            )}
          >
            {actionLabel}
            <ExternalLink className="size-3.5" aria-hidden="true" />
          </button>
        ) : (
          <p className="text-xs leading-4 text-text-tertiary">
            This preview was not published.
          </p>
        )}
      </div>
    </div>
  );
}

function appRequestDecisionFromContent(
  content: ConversationMessageContent
): AppRequestDecision | null {
  const requestType = content.requestType?.trim();
  const typedStatus =
    requestType === 'host_access'
      ? content.hostAccess?.status
      : requestType === 'computer_use_start'
        ? content.computerUseStart?.status
        : undefined;
  const status = typedStatus?.trim().toLowerCase() ?? normalizedStatus(content);
  if (!status || status === 'pending') {
    return null;
  }

  if (requestType === 'host_access') {
    return {
      approved:
        content.hostAccess?.approved ??
        (status === 'approved' ||
          status === 'success' ||
          status === 'completed'),
      reason: content.reason ?? (status === 'expired' ? 'expired' : status),
    };
  }

  if (requestType === 'computer_use_start') {
    return {
      approved:
        content.computerUseStart?.approved ??
        (status === 'approved' ||
          status === 'success' ||
          status === 'completed'),
      reason: content.reason ?? (status === 'expired' ? 'expired' : status),
      mode: content.computerUseStart?.mode,
    };
  }

  return {
    approved:
      status === 'approved' || status === 'success' || status === 'completed',
    reason: content.reason ?? (status === 'expired' ? 'expired' : status),
  };
}

function appRequestCardTone({
  isApproved,
  isExpired,
  isReplied,
}: {
  isApproved: boolean;
  isExpired: boolean;
  isReplied: boolean;
}): AppRequestTone {
  if (!isReplied) return 'pending';
  if (isApproved) return 'approved';
  if (isExpired) return 'expired';
  return 'denied';
}

function splitAppRequestDescription(description: string): {
  body: string;
  requestedEnvironment: string | null;
} {
  const [body, requestedEnvironment] = description.split(
    /\nRequested environment:\s*/
  );

  return {
    body: body?.trim() ?? description.trim(),
    requestedEnvironment: requestedEnvironment?.trim() || null,
  };
}

function stripAppRequestTextPrefix(
  text: string,
  requestType: string | undefined
) {
  const trimmed = text.trim();
  switch (requestType) {
    case 'host_access':
      return trimmed.replace(/^Permission requested:\s*/i, '').trim();
    case 'computer_use_start':
      return trimmed
        .replace(/^ComputerUse start requested(?: for [^.]*)?\.\s*/i, '')
        .trim();
    default:
      return trimmed;
  }
}

function appRequestBody(content: ConversationMessageContent) {
  const requestType = content.requestType?.trim();
  const typedDescription =
    requestType === 'host_access'
      ? content.hostAccess?.description?.trim()
      : undefined;
  const raw =
    typedDescription ||
    content.description?.trim() ||
    content.reason?.trim() ||
    content.text?.trim() ||
    'App request updated.';
  const { body } = splitAppRequestDescription(raw);
  return stripAppRequestTextPrefix(body, requestType) || raw;
}

function appRequestCardContent(
  content: ConversationMessageContent,
  t: ReturnType<typeof useT>
) {
  const requestType = content.requestType?.trim();
  const body = appRequestBody(content);
  const environmentLabel =
    requestType === 'host_access'
      ? content.hostAccess?.environmentLabel?.trim() ||
        content.environmentLabel?.trim()
      : requestType === 'computer_use_start'
        ? content.computerUseStart?.environmentLabel?.trim() ||
          content.environmentLabel?.trim()
        : content.environmentLabel?.trim();

  if (requestType === 'computer_use_start') {
    return {
      title: t('Agent wants to start ComputerUse'),
      subtitle: environmentLabel || undefined,
      body,
    };
  }

  if (requestType === 'host_access') {
    return {
      title: environmentLabel || t('Permission request'),
      subtitle: undefined,
      body,
    };
  }

  if (requestType === 'oauth_authorization') {
    const provider = content.provider?.trim();
    const alias = content.alias?.trim();
    return {
      title: 'Authorization request',
      subtitle: alias && provider ? `${provider}/${alias}` : provider,
      body,
    };
  }

  if (requestType === 'interactive_login') {
    return {
      title: 'Login request',
      subtitle: undefined,
      body,
    };
  }

  if (requestType === 'location') {
    return {
      title: 'Location request',
      subtitle: undefined,
      body,
    };
  }

  return {
    title: 'App request',
    subtitle: undefined,
    body,
  };
}

function modeDisplayName(mode: string, t: ReturnType<typeof useT>): string {
  switch (mode) {
    case 'foreground':
      return t('Foreground');
    case 'background':
      return t('Background');
    default:
      return mode.charAt(0).toUpperCase() + mode.slice(1);
  }
}

function AppRequestButton({
  children,
  onClick,
  variant = 'primary',
  disabled = false,
  loading = false,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[0.625rem] px-4 py-1.5 text-[0.8125rem] font-medium leading-[1.0625rem]',
        'appearance-none shadow-none',
        'border border-border',
        variant === 'primary'
          ? 'bg-control-bg text-control-fg hover:bg-control-bg-hover active:bg-control-bg-active'
          : 'bg-transparent text-text-secondary hover:bg-fill-soft active:bg-fill-medium',
        disabled || loading ? 'opacity-50' : '',
        'disabled:cursor-default',
        'transition-all',
        className
      )}
    >
      {loading ? (
        <span
          aria-hidden="true"
          className="size-3 animate-spin rounded-full border border-current border-t-transparent"
        />
      ) : null}
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap [&_svg]:shrink-0">
        {children}
      </span>
    </button>
  );
}

type AppRequestCompletingAction =
  | 'oauth_confirm'
  | 'interactive_login_complete'
  | 'host_access_approve'
  | 'host_access_deny'
  | 'computer_use_start_deny'
  | `computer_use_start_approve:${string}`;

function AppRequestBlock({
  content,
  onApproveLocationRequest,
  onConfirmOAuthAuthorizationRequest,
  onCompleteInteractiveLoginRequest,
  onDecideHostAccessRequest,
  onDecideComputerUseStartRequest,
}: {
  content: ConversationMessageContent;
  onApproveLocationRequest?: (requestId: string) => void;
  onConfirmOAuthAuthorizationRequest?: (
    requestId: string
  ) => void | Promise<void>;
  onCompleteInteractiveLoginRequest?: (
    requestId: string
  ) => void | Promise<void>;
  onDecideHostAccessRequest?: (
    requestId: string,
    decision: HostAccessDecisionRequest
  ) => void | Promise<void>;
  onDecideComputerUseStartRequest?: (
    requestId: string,
    decision: ComputerUseStartDecisionRequest
  ) => void | Promise<void>;
}) {
  const t = useT();
  const [completingAction, setCompletingAction] =
    useState<AppRequestCompletingAction | null>(null);
  const [completionError, setCompletionError] = useState<string | null>(null);
  const [awaitingCompletion, setAwaitingCompletion] = useState(false);
  const isCompleting = completingAction !== null;
  const status = normalizedStatus(content);
  const pending = !status || status === 'pending';
  const requestType = content.requestType?.trim();
  const decision = appRequestDecisionFromContent(content);
  const isReplied = !!decision;
  const isApproved = decision?.approved === true;
  const isExpired = decision?.reason === 'expired';
  const tone = appRequestCardTone({ isApproved, isExpired, isReplied });
  const cardContent = appRequestCardContent(content, t);
  const canApproveLocation =
    requestType === 'location' &&
    !!content.requestId &&
    pending &&
    !!onApproveLocationRequest;
  const canConfirmOAuthAuthorization =
    requestType === 'oauth_authorization' &&
    !!content.requestId &&
    pending &&
    !!onConfirmOAuthAuthorizationRequest;
  const canCompleteInteractiveLogin =
    requestType === 'interactive_login' &&
    !!content.requestId &&
    pending &&
    !!onCompleteInteractiveLoginRequest;
  const hostAccessActionsDisabled =
    isCompleting ||
    awaitingCompletion ||
    !content.requestId ||
    !onDecideHostAccessRequest;
  const computerUseActionsDisabled =
    isCompleting ||
    awaitingCompletion ||
    !content.requestId ||
    !onDecideComputerUseStartRequest;
  const oauthAuthorizationActionsDisabled =
    isCompleting ||
    awaitingCompletion ||
    !content.requestId ||
    !onConfirmOAuthAuthorizationRequest;
  const interactiveLoginActionsDisabled =
    isCompleting ||
    awaitingCompletion ||
    !content.requestId ||
    !onCompleteInteractiveLoginRequest;
  const actionURL =
    requestType === 'oauth_authorization'
      ? content.authorizationURL
      : requestType === 'interactive_login'
        ? content.loginURL
        : undefined;
  const confirmOAuthAuthorization = useCallback(async () => {
    if (isCompleting || awaitingCompletion) {
      return;
    }
    if (!content.requestId || !onConfirmOAuthAuthorizationRequest) {
      setCompletionError(
        'OAuth authorization confirmation is not available here.'
      );
      return;
    }

    setCompletionError(null);
    setCompletingAction('oauth_confirm');
    try {
      await onConfirmOAuthAuthorizationRequest(content.requestId);
      setAwaitingCompletion(true);
    } catch (error) {
      setCompletionError(
        error instanceof Error ? error.message : String(error)
      );
    } finally {
      setCompletingAction(null);
    }
  }, [
    awaitingCompletion,
    content.requestId,
    isCompleting,
    onConfirmOAuthAuthorizationRequest,
  ]);
  const completeInteractiveLogin = useCallback(async () => {
    if (isCompleting || awaitingCompletion) {
      return;
    }
    if (!content.requestId || !onCompleteInteractiveLoginRequest) {
      setCompletionError('Interactive login completion is not available here.');
      return;
    }

    setCompletionError(null);
    setCompletingAction('interactive_login_complete');
    try {
      await onCompleteInteractiveLoginRequest(content.requestId);
      setAwaitingCompletion(true);
    } catch (error) {
      setCompletionError(
        error instanceof Error ? error.message : String(error)
      );
    } finally {
      setCompletingAction(null);
    }
  }, [
    awaitingCompletion,
    content.requestId,
    isCompleting,
    onCompleteInteractiveLoginRequest,
  ]);
  const decideHostAccess = useCallback(
    async (approved: boolean) => {
      if (isCompleting || awaitingCompletion) {
        return;
      }
      if (!content.requestId || !onDecideHostAccessRequest) {
        setCompletionError('Host access decision is not available here.');
        return;
      }

      setCompletionError(null);
      setCompletingAction(
        approved ? 'host_access_approve' : 'host_access_deny'
      );
      try {
        await onDecideHostAccessRequest(content.requestId, { approved });
        setAwaitingCompletion(true);
      } catch (error) {
        setCompletionError(
          error instanceof Error ? error.message : String(error)
        );
      } finally {
        setCompletingAction(null);
      }
    },
    [
      awaitingCompletion,
      content.requestId,
      isCompleting,
      onDecideHostAccessRequest,
    ]
  );
  const decideComputerUseStart = useCallback(
    async (decisionPayload: ComputerUseStartDecisionRequest) => {
      if (isCompleting || awaitingCompletion) {
        return;
      }
      if (!content.requestId || !onDecideComputerUseStartRequest) {
        setCompletionError('ComputerUse decision is not available here.');
        return;
      }

      setCompletionError(null);
      setCompletingAction(
        decisionPayload.approved
          ? `computer_use_start_approve:${decisionPayload.mode ?? ''}`
          : 'computer_use_start_deny'
      );
      try {
        await onDecideComputerUseStartRequest(
          content.requestId,
          decisionPayload
        );
        setAwaitingCompletion(true);
      } catch (error) {
        setCompletionError(
          error instanceof Error ? error.message : String(error)
        );
      } finally {
        setCompletingAction(null);
      }
    },
    [
      awaitingCompletion,
      content.requestId,
      isCompleting,
      onDecideComputerUseStartRequest,
    ]
  );

  let icon: ReactNode;
  switch (requestType) {
    case 'oauth_authorization':
      icon = <KeyRound className="size-4" />;
      break;
    case 'interactive_login':
      icon = <LogIn className="size-4" />;
      break;
    case 'computer_use_start':
      icon = <Monitor className="size-4" />;
      break;
    case 'location':
      icon = <MapPin className="size-4" />;
      break;
    case 'host_access':
    default:
      icon = <ShieldLefthalfFilledSFSymbolMedium className="text-base" />;
      break;
  }

  const requestProblemMessage =
    pending &&
    requestType === 'oauth_authorization' &&
    (!content.requestId || !onConfirmOAuthAuthorizationRequest)
      ? !content.requestId
        ? 'App request is missing a request id.'
        : 'OAuth authorization confirmation is not available here.'
      : pending &&
          requestType === 'interactive_login' &&
          (!content.requestId || !onCompleteInteractiveLoginRequest)
        ? !content.requestId
          ? 'App request is missing a request id.'
          : 'Interactive login completion is not available here.'
        : pending &&
            requestType === 'host_access' &&
            (!content.requestId || !onDecideHostAccessRequest)
          ? !content.requestId
            ? 'App request is missing a request id.'
            : 'Host access decision is not available here.'
          : pending &&
              requestType === 'computer_use_start' &&
              (!content.requestId || !onDecideComputerUseStartRequest)
            ? !content.requestId
              ? 'App request is missing a request id.'
              : 'ComputerUse decision is not available here.'
            : null;
  const errorMessage = completionError ?? requestProblemMessage;

  let footer: ReactNode = null;
  if (requestType === 'host_access') {
    footer = (
      <div className="flex flex-wrap gap-2 pt-1">
        {isReplied ? (
          <AppRequestButton disabled>
            {isApproved
              ? t('Approved')
              : isExpired
                ? t('Expired')
                : t('Denied')}
          </AppRequestButton>
        ) : (
          <>
            <AppRequestButton
              disabled={hostAccessActionsDisabled}
              loading={completingAction === 'host_access_approve'}
              onClick={() => void decideHostAccess(true)}
            >
              {t('Approve')}
            </AppRequestButton>
            <AppRequestButton
              disabled={hostAccessActionsDisabled}
              loading={completingAction === 'host_access_deny'}
              onClick={() => void decideHostAccess(false)}
              variant="secondary"
            >
              {t('Deny')}
            </AppRequestButton>
          </>
        )}
      </div>
    );
  } else if (requestType === 'computer_use_start') {
    if (isReplied) {
      const summary = isApproved
        ? decision?.mode
          ? t('Started in {mode} mode', {
              mode: modeDisplayName(decision.mode, t),
            })
          : t('Started')
        : isExpired
          ? t('Expired')
          : t('Denied');
      footer = (
        <div className="flex flex-wrap gap-2 pt-1">
          <AppRequestButton disabled>{summary}</AppRequestButton>
        </div>
      );
    } else {
      const availableModes = content.computerUseStart?.availableModes ?? [];
      footer = (
        <div className="space-y-3">
          {(content.computerUseStart?.apps ?? []).length > 0 ? (
            <p className="text-xs leading-4 text-text-tertiary">
              {t('Apps in focus: {apps}', {
                apps: content.computerUseStart?.apps?.join(', ') ?? '',
              })}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {availableModes.map(mode => (
              <AppRequestButton
                key={mode}
                disabled={computerUseActionsDisabled}
                loading={
                  completingAction === `computer_use_start_approve:${mode}`
                }
                onClick={() =>
                  void decideComputerUseStart({
                    approved: true,
                    mode,
                  })
                }
              >
                {modeDisplayName(mode, t)}
              </AppRequestButton>
            ))}
            <AppRequestButton
              disabled={computerUseActionsDisabled}
              loading={completingAction === 'computer_use_start_deny'}
              onClick={() =>
                void decideComputerUseStart({
                  approved: false,
                })
              }
              variant="secondary"
            >
              {t('Deny')}
            </AppRequestButton>
          </div>
        </div>
      );
    }
  } else {
    footer = (
      <div className="flex flex-wrap gap-2 pt-1">
        {actionURL && pending ? (
          <AppRequestButton onClick={() => openAppRequestURL(actionURL)}>
            {requestType === 'oauth_authorization' ? 'Authorize' : 'Open login'}
            <ExternalLink className="size-3" aria-hidden="true" />
          </AppRequestButton>
        ) : null}
        {canApproveLocation ? (
          <AppRequestButton
            onClick={() => onApproveLocationRequest?.(content.requestId!)}
          >
            Share current location
          </AppRequestButton>
        ) : null}
        {canConfirmOAuthAuthorization ? (
          <AppRequestButton
            loading={completingAction === 'oauth_confirm'}
            disabled={oauthAuthorizationActionsDisabled}
            onClick={() => {
              void confirmOAuthAuthorization();
            }}
          >
            I authorized
          </AppRequestButton>
        ) : null}
        {canCompleteInteractiveLogin ? (
          <AppRequestButton
            loading={completingAction === 'interactive_login_complete'}
            disabled={interactiveLoginActionsDisabled}
            onClick={() => {
              void completeInteractiveLogin();
            }}
          >
            I signed in
          </AppRequestButton>
        ) : null}
      </div>
    );
  }

  return (
    <div
      data-app-request-card
      className={cn(
        'w-full',
        'select-none overflow-hidden rounded-2xl',
        'border',
        'px-4 py-4',
        tone === 'approved'
          ? 'border-success-fg/20 bg-success-bg'
          : tone === 'expired'
            ? 'border-border bg-surface-card-muted'
            : tone === 'denied'
              ? 'border-warning-fg/20 bg-warning-bg'
              : 'border-border bg-surface-card'
      )}
    >
      <div className="flex flex-col gap-3">
        <div className="flex size-10 items-center justify-center rounded-2xl bg-fill-soft text-text-primary">
          {icon}
        </div>

        <div className="min-w-0 space-y-1">
          <div className="text-[0.9375rem] font-semibold leading-5 text-text-primary">
            {cardContent.title}
          </div>
          {cardContent.subtitle ? (
            <div className="text-xs font-medium leading-4 text-text-tertiary">
              {cardContent.subtitle}
            </div>
          ) : null}
        </div>

        <p className="whitespace-pre-line text-[0.9375rem] leading-5.5 text-text-secondary">
          {cardContent.body}
        </p>

        {errorMessage ? (
          <p role="alert" className="text-xs leading-4 text-warning-fg">
            {errorMessage}
          </p>
        ) : null}

        {footer}
      </div>
    </div>
  );
}

export type AssistantGroupItem =
  | { type: 'message'; message: ConversationMessage }
  | { type: 'state'; state: AssistantState };

export type ToolMessageRenderer = (
  message: ConversationMessage
) => ReactNode | null | undefined;

export type AssistantBubbleStyle = 'plain' | 'bubble';

function parseStatusCode(errorMessage: string): number | undefined {
  const match = errorMessage.match(/\b([1-5]\d{2})\b/);
  if (!match) return undefined;
  const code = Number.parseInt(match[1], 10);
  if (Number.isNaN(code)) return undefined;
  return code;
}

function buildErrorPayload(
  messages: ConversationMessage[]
): ErrorPayload | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (!message.error) {
      // A successful, renderable message after an error means the error is stale.
      const hasRenderableContent =
        (message.content && message.content.length > 0) ||
        isQuestionMessage(message) ||
        isSaveFileReplyMessage(message) ||
        isSaveFileRequestMessage(message) ||
        isSandboxReviewMessage(message) ||
        isSecretInputMessage(message);
      if (hasRenderableContent) {
        return null;
      }
      continue;
    }

    const desc = message.error.trim();
    if (desc === '') {
      continue;
    }

    const payload: ErrorPayload = {
      desc,
      errorType: message.errorType,
    };
    if (message.errorType === 'http_status_code') {
      payload.statusCode = parseStatusCode(desc);
    }
    return payload;
  }
  return null;
}

function wrapMessageNode(
  message: ConversationMessage,
  node: ReactNode,
  key: string,
  showIdentity = true
) {
  const targetMessageId = message.messageId ?? message.id;
  return (
    <div
      key={key}
      data-message-id={targetMessageId}
      className="conversation-message-focus-target flex flex-col gap-1.5"
    >
      {showIdentity ? <MessageIdentityHeader message={message} /> : null}
      {node}
    </div>
  );
}

function getBookSharePayloadFromMessage(message: ConversationMessage) {
  const texts = getTextContent(message);
  for (const text of texts) {
    const payload = tryParseBookSharePayload(text);
    if (payload) {
      return payload;
    }
  }
  return null;
}

function isOnboardingDemoIntroMessage(message: ConversationMessage) {
  return message.id.includes('-intro-');
}

function renderContentBlock(
  message: ConversationMessage,
  content: ConversationMessageContent,
  key: string,
  isAnimating: boolean,
  contentIndex: number,
  onRequestFileAccess?: (message: string) => void | Promise<void>,
  onAttachmentImageLayout?: ConversationAttachmentImageLayoutHandler,
  onApproveLocationRequest?: (requestId: string) => void,
  onConfirmOAuthAuthorizationRequest?: (
    requestId: string
  ) => void | Promise<void>,
  onCompleteInteractiveLoginRequest?: (
    requestId: string
  ) => void | Promise<void>,
  onDecideHostAccessRequest?: (
    requestId: string,
    decision: HostAccessDecisionRequest
  ) => void | Promise<void>,
  onDecideComputerUseStartRequest?: (
    requestId: string,
    decision: ComputerUseStartDecisionRequest
  ) => void | Promise<void>
) {
  const messageId = message.messageId ?? message.id;
  switch (content.type) {
    case 'text':
      return content.text ? (
        <div key={key} data-message-id={messageId} data-quote-source="true">
          <CueStreamdown
            className="text-sm"
            animated={isAnimating}
            controls={assistantMarkdownControls}
          >
            {content.text}
          </CueStreamdown>
        </div>
      ) : null;
    case 'image': {
      const attachmentKey = conversationMessageAttachmentKey(contentIndex);
      return (
        <ConversationAttachmentView
          key={key}
          content={content}
          fallbackFileName="attachment"
          previewKind="image"
          imageClassName="h-80 w-full max-w-[26.666rem]"
          artifactId={key}
          cachedImageSize={conversationMessageCachedAttachmentImageSize(
            message,
            attachmentKey
          )}
          onImageLayout={imageSize =>
            onAttachmentImageLayout?.(
              conversationMessageRenderIdentity(message),
              attachmentKey,
              imageSize
            )
          }
          onRequestFileAccess={onRequestFileAccess}
        />
      );
    }
    case 'audio':
      return (
        <ConversationAttachmentView
          key={key}
          content={content}
          fallbackFileName="Audio file"
          previewKind="audio"
          artifactId={key}
          onRequestFileAccess={onRequestFileAccess}
        />
      );
    case 'video':
      return (
        <ConversationAttachmentView
          key={key}
          content={content}
          fallbackFileName="Video file"
          previewKind="video"
          artifactId={key}
          className="h-64 w-auto max-w-full"
          onRequestFileAccess={onRequestFileAccess}
        />
      );
    case 'file':
      return (
        <ConversationAttachmentView
          key={key}
          content={content}
          previewKind="file"
          artifactId={key}
          onRequestFileAccess={onRequestFileAccess}
        />
      );
    case 'preview':
      return <HTMLPreviewBlock key={key} content={content} />;
    case 'app_request':
      return (
        <AppRequestBlock
          key={key}
          content={content}
          onApproveLocationRequest={onApproveLocationRequest}
          onConfirmOAuthAuthorizationRequest={
            onConfirmOAuthAuthorizationRequest
          }
          onCompleteInteractiveLoginRequest={onCompleteInteractiveLoginRequest}
          onDecideHostAccessRequest={onDecideHostAccessRequest}
          onDecideComputerUseStartRequest={onDecideComputerUseStartRequest}
        />
      );
    default:
      return null;
  }
}

export const AssistantMessage = ({
  items,
  allMessages,
  assistantBubbleStyle = 'plain',
  currentAssistantStateSequence,
  userMessageId,
  isLast,
  isStreaming,
  isAnimating = false,
  showIdentity = true,
  hideOperations = false,
  renderToolMessage,
  onApproveLocationRequest,
  onConfirmOAuthAuthorizationRequest,
  onCompleteInteractiveLoginRequest,
  onDecideHostAccessRequest,
  onDecideComputerUseStartRequest,
  onAttachmentImageLayout,
  onSendMessage,
}: {
  items: AssistantGroupItem[];
  allMessages: ConversationMessage[];
  assistantBubbleStyle?: AssistantBubbleStyle;
  currentAssistantStateSequence?: number;
  userMessageId?: string;
  isLast?: boolean;
  isStreaming?: boolean;
  isAnimating?: boolean;
  showIdentity?: boolean;
  hideOperations?: boolean;
  renderToolMessage?: ToolMessageRenderer;
  onApproveLocationRequest?: (requestId: string) => void;
  onConfirmOAuthAuthorizationRequest?: (
    requestId: string
  ) => void | Promise<void>;
  onCompleteInteractiveLoginRequest?: (
    requestId: string
  ) => void | Promise<void>;
  onDecideHostAccessRequest?: (
    requestId: string,
    decision: HostAccessDecisionRequest
  ) => void | Promise<void>;
  onDecideComputerUseStartRequest?: (
    requestId: string,
    decision: ComputerUseStartDecisionRequest
  ) => void | Promise<void>;
  onAttachmentImageLayout?: ConversationAttachmentImageLayoutHandler;
  onSendMessage?: (text: string) => void | Promise<void>;
}) => {
  const messages = useMemo(
    () =>
      items
        .filter(
          (i): i is AssistantGroupItem & { type: 'message' } =>
            i.type === 'message'
        )
        .map(i => i.message),
    [items]
  );
  const messageTimestamp = useMemo(
    () => assistantTurnTimestamp(items),
    [items]
  );
  const errorPayload = useMemo(() => buildErrorPayload(messages), [messages]);
  const containsCurrentInProgressState = items.some(
    item =>
      item.type === 'state' &&
      item.state.sequence === currentAssistantStateSequence &&
      (item.state.phase === 'thinking' || item.state.phase === 'execution')
  );
  const visibleToolEntries = useMemo(() => {
    const orderedEntryIds: string[] = [];
    const entriesById = new Map<
      string,
      {
        toolUseId?: string;
        node: ReactNode;
      }
    >();

    for (const message of messages) {
      if (!isToolMessage(message)) {
        continue;
      }

      const texts = getTextContent(message);
      for (let textIndex = 0; textIndex < texts.length; textIndex += 1) {
        const text = texts[textIndex];

        const bookSharePayload = tryParseBookSharePayload(text);
        if (bookSharePayload) {
          const entryId =
            bookSharePayload.submission_id ??
            message.toolUseId ??
            message.id ??
            `book-share-${message.timestamp}-${textIndex}`;
          if (!entriesById.has(entryId)) {
            orderedEntryIds.push(entryId);
          }
          entriesById.set(entryId, {
            toolUseId: message.toolUseId ?? undefined,
            node: <BookShareCard key={entryId} payload={bookSharePayload} />,
          });
          continue;
        }

        const interactiveLoginPayload = tryParseInteractiveLoginPayload(text);
        if (interactiveLoginPayload) {
          const entryId = `interactive-login-${interactiveLoginPayload.login_id}`;
          if (!entriesById.has(entryId)) {
            orderedEntryIds.push(entryId);
          }
          entriesById.set(entryId, {
            toolUseId: message.toolUseId ?? undefined,
            node: (
              <InteractiveLoginCard
                key={entryId}
                payload={interactiveLoginPayload}
              />
            ),
          });
          continue;
        }

        const payload = tryParseWebBrowsePayload(text);
        if (payload) {
          const completed =
            message.toolUseId != null &&
            allMessages.some(
              m =>
                m !== message &&
                isToolMessage(m) &&
                m.toolUseId === message.toolUseId &&
                m.timestamp > message.timestamp
            );
          if (completed) {
            continue;
          }

          const entryId =
            message.toolUseId ??
            message.id ??
            `tool-payload-${message.timestamp}-${textIndex}`;
          if (!entriesById.has(entryId)) {
            orderedEntryIds.push(entryId);
          }
          entriesById.set(entryId, {
            toolUseId: message.toolUseId ?? undefined,
            node: (
              <WebBrowseWidget
                key={message.id || `tool-${textIndex}`}
                payload={payload}
              />
            ),
          });
          continue;
        }
      }
    }

    return orderedEntryIds
      .map(entryId => entriesById.get(entryId))
      .filter(
        (entry): entry is NonNullable<typeof entry> => entry !== undefined
      );
  }, [allMessages, messages]);
  const visibleToolNodes = useMemo(
    () =>
      visibleToolEntries.map((entry, index) => (
        <div key={`tool-entry-${index}`}>{entry.node}</div>
      )),
    [visibleToolEntries]
  );

  const hasRenderableMessageContent = useMemo(
    () =>
      messages.some(message => {
        if (isErrorMessage(message) || isToolMessage(message)) {
          return false;
        }

        return (
          (message.content?.length ?? 0) > 0 ||
          isQuestionMessage(message) ||
          isSaveFileReplyMessage(message) ||
          isSaveFileRequestMessage(message) ||
          isSandboxReviewMessage(message) ||
          isScheduleMessage(message) ||
          isSecretInputMessage(message) ||
          isAppRequestMessage(message)
        );
      }) || !!errorPayload,
    [errorPayload, messages]
  );
  const hasOnlyAppRequestMessages =
    messages.length > 0 && messages.every(isAppRequestMessage);
  const showOperations =
    hasRenderableMessageContent &&
    !hasOnlyAppRequestMessages &&
    !hideOperations &&
    !containsCurrentInProgressState &&
    !(isLast && isStreaming);

  let renderedToolStatusBlock = false;
  const renderedItems = items.flatMap((item, itemIndex) => {
    if (item.type === 'state') {
      return [];
    }

    const msg = item.message;

    if (isSandboxReviewMessage(msg)) {
      return [
        wrapMessageNode(
          msg,
          <SandboxReviewMessage message={msg} />,
          msg.id || `sr-${itemIndex}`,
          showIdentity
        ),
      ];
    }

    if (isQuestionMessage(msg)) {
      return [
        wrapMessageNode(
          msg,
          <QuestionMessage message={msg} messages={allMessages} />,
          msg.id || `q-${itemIndex}`,
          showIdentity
        ),
      ];
    }

    if (isScheduleMessage(msg)) {
      return [
        wrapMessageNode(
          msg,
          <ScheduleMessage message={msg} />,
          msg.id || `schedule-${itemIndex}`,
          showIdentity
        ),
      ];
    }

    if (isSaveFileRequestMessage(msg)) {
      return [
        wrapMessageNode(
          msg,
          <SaveFileMessage message={msg} messages={allMessages} />,
          msg.id || `save-file-${itemIndex}`,
          showIdentity
        ),
      ];
    }

    if (isSaveFileReplyMessage(msg)) {
      return [
        wrapMessageNode(
          msg,
          <SaveFileMessage message={msg} messages={allMessages} />,
          msg.id || `save-file-reply-${itemIndex}`,
          showIdentity
        ),
      ];
    }

    if (isSecretInputMessage(msg)) {
      return [
        wrapMessageNode(
          msg,
          <SecretInputMessage message={msg} messages={allMessages} />,
          msg.id || `secret-${itemIndex}`,
          showIdentity
        ),
      ];
    }

    if (isToolMessage(msg)) {
      const customNode = renderToolMessage?.(msg);
      if (customNode) {
        return [
          wrapMessageNode(
            msg,
            <div className="flex flex-col items-start gap-2">{customNode}</div>,
            msg.id || `custom-tool-${itemIndex}`,
            showIdentity
          ),
        ];
      }

      if (renderedToolStatusBlock || visibleToolNodes.length === 0) {
        return [];
      }

      renderedToolStatusBlock = true;

      return [
        wrapMessageNode(
          msg,
          <div className="flex flex-col items-start gap-1">
            {visibleToolNodes}
          </div>,
          `tool-status-${msg.id || itemIndex}`,
          showIdentity
        ),
      ];
    }

    if (isErrorMessage(msg)) {
      // Pure error messages — handled by errorPayload below
      return [];
    }

    const bookSharePayload = getBookSharePayloadFromMessage(msg);
    if (bookSharePayload) {
      return [
        wrapMessageNode(
          msg,
          <BookShareCard payload={bookSharePayload} />,
          msg.id || `book-share-${itemIndex}`,
          showIdentity
        ),
      ];
    }

    // Assistant message: render content blocks.
    const rawContents = msg.content ?? [];
    const contents = rawContents
      .map((content, ci) => {
        return renderContentBlock(
          msg,
          content,
          `${msg.id || itemIndex}-${ci}`,
          isAnimating,
          ci,
          onSendMessage,
          onAttachmentImageLayout,
          onApproveLocationRequest,
          onConfirmOAuthAuthorizationRequest,
          onCompleteInteractiveLoginRequest,
          onDecideHostAccessRequest,
          onDecideComputerUseStartRequest
        );
      })
      .filter(Boolean);

    if (contents.length === 0) {
      return [];
    }

    return [
      <div
        key={msg.id || `assistant-message-${itemIndex}`}
        className={cn(
          'conversation-message-focus-target flex flex-col gap-1.5',
          isOnboardingDemoIntroMessage(msg) &&
            'animate-in fade-in-0 slide-in-from-bottom-1 duration-300',
          assistantBubbleStyle === 'bubble' &&
            'w-fit max-w-[min(78%,34rem)] rounded-[1.125rem] border border-border/60 bg-card/80 px-3.5 py-2.5 text-card-foreground shadow-[0_0.625rem_1.75rem_rgba(15,23,42,0.08)] backdrop-blur-md'
        )}
        data-message-id={msg.messageId ?? msg.id}
      >
        {showIdentity ? <MessageIdentityHeader message={msg} /> : null}
        <div className="flex flex-col gap-2">{contents}</div>
      </div>,
    ];
  });

  return (
    <div className="assistant-message-turn flex flex-col gap-1">
      <ErrorBoundary>
        <div className="flex flex-col gap-2">
          {errorPayload ? (
            <ErrorCard
              error={errorPayload}
              onRetry={
                window.jsb?.MessagesBridge?.retryMessage
                  ? () => {
                      window.jsb.MessagesBridge.retryMessage().catch(() => {
                        console.error(
                          '[AssistantMessage] retry message failed'
                        );
                      });
                    }
                  : undefined
              }
            />
          ) : null}

          {renderedItems}
        </div>
      </ErrorBoundary>

      {showOperations ? (
        <AssistantMessageOperations
          messages={messages}
          messageTimestamp={messageTimestamp}
          userMessageId={userMessageId}
        />
      ) : null}
    </div>
  );
};
